/**
 * KATA Board Component
 * Interactive PDCA cycle management with drag-and-drop
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { 
  Play, 
  Pause, 
  CheckCircle, 
  Clock, 
  Users, 
  Target, 
  TrendingUp,
  Plus,
  MoreVertical,
  Calendar,
  AlertCircle,
  Lightbulb,
  FileText,
  BarChart3
} from 'lucide-react';
import { useKATABoardStore } from '../../stores/kataBoardStore';
import { kataUtils } from '../../services/kataBoard';
import PDCAStepCard from './PDCAStepCard';
import ExperimentCard from './ExperimentCard';
import KATANoteCard from './KATANoteCard';
import CreateCycleModal from './CreateCycleModal';
import CreateExperimentModal from './CreateExperimentModal';
import CreateNoteModal from './CreateNoteModal';
import LoadingSpinner from '../common/LoadingSpinner';

const KATABoard: React.FC = () => {
  const { workshopId } = useParams<{ workshopId: string }>();
  const [activeTab, setActiveTab] = useState<'board' | 'cycles' | 'analytics'>('board');
  
  const {
    currentWorkshop,
    boardData,
    currentCycle,
    loading,
    error,
    showCreateCycleModal,
    showCreateExperimentModal,
    showCreateNoteModal,
    fetchWorkshopBoard,
    setShowCreateCycleModal,
    setShowCreateExperimentModal,
    setShowCreateNoteModal,
    advanceStep,
    markStepCurrent,
    clearError
  } = useKATABoardStore();

  useEffect(() => {
    if (workshopId) {
      fetchWorkshopBoard(parseInt(workshopId));
    }
  }, [workshopId, fetchWorkshopBoard]);

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId, type } = result;

    // If dropped outside a droppable area
    if (!destination) return;

    // If dropped in the same position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Handle step reordering or status changes
    if (type === 'STEP') {
      const stepId = parseInt(draggableId.replace('step-', ''));
      
      // If moving to a different status column
      if (destination.droppableId !== source.droppableId) {
        const newStatus = destination.droppableId.replace('steps-', '');
        
        // Mark step as current if moved to in_progress
        if (newStatus === 'in_progress') {
          await markStepCurrent(stepId);
        }
      }
    }
  };

  if (loading && !boardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
          <p className="text-red-800">{error}</p>
          <button
            onClick={clearError}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  if (!boardData || !currentWorkshop) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No KATA Board Found</h3>
        <p className="text-gray-500 mb-4">This workshop doesn't have a KATA board yet.</p>
        <button
          onClick={() => setShowCreateCycleModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Create First KATA Cycle
        </button>
      </div>
    );
  }

  const stepsByStatus = currentCycle?.pdca_steps.reduce((acc, step) => {
    const status = step.is_current ? 'in_progress' : step.status;
    if (!acc[status]) acc[status] = [];
    acc[status].push(step);
    return acc;
  }, {} as Record<string, typeof currentCycle.pdca_steps>) || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {currentWorkshop.title} - KATA Board
            </h1>
            <p className="text-gray-600 mt-1">{currentWorkshop.improvement_description}</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowCreateCycleModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Cycle
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center">
              <Target className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-blue-900">Total Cycles</p>
                <p className="text-2xl font-bold text-blue-600">
                  {boardData.board_stats.total_cycles}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center">
              <Play className="h-8 w-8 text-green-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-green-900">Active Cycles</p>
                <p className="text-2xl font-bold text-green-600">
                  {boardData.board_stats.active_cycles}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center">
              <BarChart3 className="h-8 w-8 text-purple-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-purple-900">Experiments</p>
                <p className="text-2xl font-bold text-purple-600">
                  {boardData.board_stats.total_experiments}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-orange-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-orange-900">Success Rate</p>
                <p className="text-2xl font-bold text-orange-600">
                  {boardData.board_stats.total_experiments > 0 
                    ? Math.round((boardData.board_stats.successful_experiments / boardData.board_stats.total_experiments) * 100)
                    : 0}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'board', label: 'KATA Board', icon: Target },
              { id: 'cycles', label: 'All Cycles', icon: Clock },
              { id: 'analytics', label: 'Analytics', icon: BarChart3 }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'board' && currentCycle && (
            <DragDropContext onDragEnd={handleDragEnd}>
              <div className="space-y-6">
                {/* Current Cycle Info */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {currentCycle.title}
                      </h2>
                      <p className="text-gray-600 mt-1">Cycle #{currentCycle.cycle_number}</p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Progress</p>
                        <p className="text-lg font-semibold text-blue-600">
                          {currentCycle.progress_percentage}%
                        </p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                        currentCycle.status === 'completed' 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {kataUtils.getStatusDisplay(currentCycle.status)}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Current Condition</h4>
                      <p className="text-gray-700 text-sm bg-white rounded-lg p-3">
                        {currentCycle.current_condition}
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Target Condition</h4>
                      <p className="text-gray-700 text-sm bg-white rounded-lg p-3">
                        {currentCycle.target_condition}
                      </p>
                    </div>
                  </div>

                  {currentCycle.obstacle && (
                    <div className="mt-4">
                      <h4 className="font-medium text-gray-900 mb-2">Current Obstacle</h4>
                      <p className="text-gray-700 text-sm bg-white rounded-lg p-3">
                        {currentCycle.obstacle}
                      </p>
                    </div>
                  )}
                </div>

                {/* PDCA Steps Board */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                  {['plan', 'do', 'check', 'act'].map((stepType) => (
                    <div key={stepType} className="space-y-4">
                      <div className={`text-center py-3 rounded-lg font-medium text-white ${
                        kataUtils.getStepTypeColor(stepType) === 'blue' ? 'bg-blue-600' :
                        kataUtils.getStepTypeColor(stepType) === 'green' ? 'bg-green-600' :
                        kataUtils.getStepTypeColor(stepType) === 'orange' ? 'bg-orange-600' :
                        'bg-purple-600'
                      }`}>
                        {kataUtils.getStepTypeDisplay(stepType)}
                      </div>

                      <Droppable droppableId={`steps-${stepType}`} type="STEP">
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`min-h-[200px] rounded-lg border-2 border-dashed p-4 transition-colors ${
                              snapshot.isDraggingOver
                                ? 'border-blue-400 bg-blue-50'
                                : 'border-gray-200 bg-gray-50'
                            }`}
                          >
                            {currentCycle.pdca_steps
                              .filter(step => step.step_type === stepType)
                              .map((step, index) => (
                                <Draggable
                                  key={step.id}
                                  draggableId={`step-${step.id}`}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className={`mb-4 ${
                                        snapshot.isDragging ? 'rotate-2 shadow-lg' : ''
                                      }`}
                                    >
                                      <PDCAStepCard 
                                        step={step}
                                        onAddExperiment={() => setShowCreateExperimentModal(true)}
                                        onAddNote={() => setShowCreateNoteModal(true)}
                                      />
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  ))}
                </div>

                {/* Cycle Actions */}
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => advanceStep(currentCycle.id)}
                    className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
                    disabled={currentCycle.status === 'completed'}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Advance Step
                  </button>
                  
                  <button
                    onClick={() => setShowCreateNoteModal(true)}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Add Note
                  </button>
                </div>
              </div>
            </DragDropContext>
          )}

          {activeTab === 'board' && !currentCycle && (
            <div className="text-center py-12">
              <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Cycle</h3>
              <p className="text-gray-500 mb-4">Start a new KATA cycle to begin improvement work.</p>
              <button
                onClick={() => setShowCreateCycleModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create KATA Cycle
              </button>
            </div>
          )}

          {activeTab === 'cycles' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">All KATA Cycles</h3>
                <button
                  onClick={() => setShowCreateCycleModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Cycle
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {boardData.kata_cycles.map((cycle) => (
                  <div key={cycle.id} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{cycle.title}</h4>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        cycle.status === 'completed' 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {kataUtils.getStatusDisplay(cycle.status)}
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-3">{cycle.description}</p>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>Cycle #{cycle.cycle_number}</span>
                      <span>{cycle.progress_percentage}% complete</span>
                    </div>
                    
                    <div className="mt-3 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${cycle.progress_percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">KATA Analytics</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white border rounded-lg p-6">
                  <h4 className="font-medium text-gray-900 mb-4">Cycle Performance</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Average Duration</span>
                      <span className="font-medium">
                        {boardData.kata_cycles.length > 0 ? '2.3 weeks' : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Success Rate</span>
                      <span className="font-medium text-green-600">
                        {boardData.board_stats.total_experiments > 0 
                          ? Math.round((boardData.board_stats.successful_experiments / boardData.board_stats.total_experiments) * 100)
                          : 0}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border rounded-lg p-6">
                  <h4 className="font-medium text-gray-900 mb-4">Experiment Insights</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Experiments</span>
                      <span className="font-medium">{boardData.board_stats.total_experiments}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Successful</span>
                      <span className="font-medium text-green-600">
                        {boardData.board_stats.successful_experiments}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border rounded-lg p-6">
                  <h4 className="font-medium text-gray-900 mb-4">Team Engagement</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Active Coaches</span>
                      <span className="font-medium">
                        {new Set(boardData.kata_cycles.filter(c => c.coach).map(c => c.coach!.id)).size}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Active Learners</span>
                      <span className="font-medium">
                        {new Set(boardData.kata_cycles.filter(c => c.learner).map(c => c.learner!.id)).size}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreateCycleModal && (
        <CreateCycleModal 
          workshopId={parseInt(workshopId!)}
          onClose={() => setShowCreateCycleModal(false)}
        />
      )}

      {showCreateExperimentModal && (
        <CreateExperimentModal 
          onClose={() => setShowCreateExperimentModal(false)}
        />
      )}

      {showCreateNoteModal && (
        <CreateNoteModal 
          onClose={() => setShowCreateNoteModal(false)}
        />
      )}
    </div>
  );
};

export default KATABoard;
